import { useEffect, useState } from 'react';

import { useSelector } from 'react-redux';

import useCodePreview from '@/pages/DataHub/Export/ExportPreview/useCodePreview';
import useDownloadFileContent from '@/pages/DataHub/Export/ExportPreview/useDownloadFileContent';
import { useExportSettingsContext } from '@/pages/DataHub/Export/ExportPreview/useExportSettingsContext';
import { replacePlaceholders } from '@/pages/DataHub/Export/utils/replacePlaceholders';
import useTokenGeneration from '@/pages/Profile/TokenGeneration/useTokenGeneration';
import { RootState } from '@/store';
import { SelectedSymbol } from '@/types/symbols';

const DEFAULT_GROUP_NAME = 'Prod_Indexes';
const usePythonPreview = (capturePosthogEvent: () => void) => {
  const { exportType, includeMetadata, from, to, period, periodNumber } =
    useExportSettingsContext();
  const { dataHubs, activeDataHubId } = useSelector((state: RootState) => state.dataHub);
  const dataHubSymbols = dataHubs.find(dataHub => dataHub.id === activeDataHubId)?.symbols || [];
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const { apiKeys } = useTokenGeneration();
  const sortedKeys = [...apiKeys]?.sort(
    (a, b) => new Date(a.creationDate).getTime() - new Date(b.creationDate).getTime()
  );
  const apiKey = sortedKeys?.slice(-1)[0]?.prefixedRawApiKey ?? '<YOUR_GX_API_TOKEN>';
  const [fileContent, setFileContent] = useState<string>('');

  const { userConfig } = useSelector((state: RootState) => state.userConfig);
  const userTimezone = userConfig.timezone.value;
  const { createRequestToValue, createRequestFromValue } = useCodePreview(
    exportType,
    from,
    to,
    period,
    periodNumber
  );

  const { downloadFileContent } = useDownloadFileContent(capturePosthogEvent);

  const downloadFile = () => {
    downloadFileContent(fileContent, 'python');
  };

  useEffect(() => {
    getDataHubIndexes();
  }, [dataHubSymbols, from, to, includeMetadata, period, periodNumber, exportType]);

  const getDataHubIndexes = async () => {
    setLoading(true);
    setError(false);

    try {
      const results = await fetch('/export/examples/python_export_script_template.txt');
      const pythonScriptTemplate = await results.text();
      const toValue = createRequestToValue();
      const fromValue = createRequestFromValue();
      // Define placeholder replacements
      const replacements = {
        symbols: JSON.stringify(transformSymbols(dataHubSymbols), undefined, 4),
        rangeStart: fromValue,
        rangeEnd: toValue,
        timezone: userTimezone,
        withMetadata: includeMetadata.toString(),
        apiKey
      };

      const pythonScript = replacePlaceholders(pythonScriptTemplate, replacements);
      setFileContent(pythonScript);
    } catch (error) {
      console.error(error);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  // Function to transform the array into the desired JSON format
  function transformSymbols(selectedSymbols: SelectedSymbol[]) {
    return {
      symbols: selectedSymbols.map(symbol => ({
        group: DEFAULT_GROUP_NAME,
        key: {
          code: symbol.Code,
          periodType: symbol.PeriodType,
          timeRef: symbol.TimeRef,
          period: symbol.Period
        }
      }))
    };
  }

  return { loading, error, fileContent, downloadFile };
};

export default usePythonPreview;
